import { useEffect, useRef, useState } from "react";
import Input from "./Input";
import { emptyFilters } from "../../utils/unitListQuery";

const optionToSlug = (option) => String(option).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const FilterDropDown = ({
  sections = [],
  values = {},
  onApply,
  onClearAll,
  "data-testid": dataTestId = "filter-dropdown",
}) => {
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [draftValues, setDraftValues] = useState(() => ({ ...emptyFilters(sections), ...values }));
  const dropdownRef = useRef(null);

  const openDropdown = () => {
    setDraftValues({ ...emptyFilters(sections), ...values });
    setShowFilterOptions(true);
  };

  const toggleDropdown = () => {
    if (showFilterOptions) {
      setShowFilterOptions(false);
      return;
    }
    openDropdown();
  };

  const setDraftValue = (key, value) => {
    setDraftValues((previous) => ({ ...previous, [key]: value }));
  };

  const hasDraftSelection = Object.values(draftValues).some((value) => value !== "" && value != null);

  const clearAll = () => {
    setDraftValues(emptyFilters(sections));
    onClearAll?.();
    setShowFilterOptions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowFilterOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderOption = (option, selected, onSelect, idPrefix) => (
    <li
      key={option.label}
      data-testid={`${dataTestId}-${idPrefix}-option-${optionToSlug(option.label)}`}
      className={`px-3 py-2.5 text-black dark:text-white text-xs text-left leading-4 lg:text-sm lg:leading-5 font-normal tracking-[-0.408px] border-b border-grey dark:border-darkThemeBorder cursor-pointer
        ${selected === option.value ? 'bg-[#FFF1C6]  dark:bg-darkThemeDropdownItemBg flex justify-between items-center gap-2' : ''}`}
      onClick={(e) => {
        e.stopPropagation(); // Prevent dropdown toggle
        onSelect(option.value); // Only select, do not close
      }}
    >
      <span>{option.label}</span>
      {selected === option.value && (
        <span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M8.49215 15C8.24102 15 8.00244 14.8995 7.82665 14.7238L4.27311 11.1702C3.90896 10.8061 3.90896 10.2033 4.27311 9.8392C4.63725 9.47506 5.23997 9.47506 5.60412 9.8392L8.49215 12.7272L14.9463 6.27311C15.3104 5.90896 15.9132 5.90896 16.2773 6.27311C16.6414 6.63725 16.6414 7.23997 16.2773 7.60412L9.15766 14.7238C8.98186 14.8995 8.74329 15 8.49215 15Z"
              fill="#FF8C65"
            />
          </svg>
        </span>
      )}
    </li>
  );

  const renderRangeInput = (section, key, placeholder) => (
    <Input
      type="number"
      value={draftValues[key] ?? ""}
      onChange={(event) => setDraftValue(key, event.target.value)}
      placeholder={placeholder}
      data-testid={`${dataTestId}-${optionToSlug(section.title)}-${optionToSlug(placeholder)}-input`}
      className="w-full h-9 rounded border border-grey dark:border-darkThemeBorder bg-white dark:bg-transparent dark:text-white px-2 text-sm leading-5 font-normal text-black"
    />
  );

  const renderSection = (section) => (
    <div key={section.key}>
      <h5 className="sortingCatagryTitle text-[#333] dark:text-white text-base font-semibold leading-6 tracking-[0.08px] px-3 pt-4 pb-3 border-b border-grey dark:border-darkThemeBorder">
        {section.title}
      </h5>
      {section.type === "range" ? (
        <div className="flex items-center gap-2 px-3 pt-3">
          {renderRangeInput(section, section.minKey, "Min")}
          {renderRangeInput(section, section.maxKey, "Max")}
        </div>
      ) : (
        <ul className="bg-white dark:bg-transparent rounded-lg w-full">
          {section.options.map((option) =>
            renderOption(option, draftValues[section.key] ?? "", (value) => setDraftValue(section.key, value), section.key)
          )}
        </ul>
      )}
    </div>
  );

  return (
    <div ref={dropdownRef} className="filterHolder bg-white dark:darkThemeGradiantBg shadow-appShadow px-2 py-1 flex justify-between items-center gap-2 h-9 rounded-md relative" data-testid={dataTestId}>
      <div
        className="filter cursor-pointer"
        onClick={toggleDropdown}
        data-testid={`${dataTestId}-trigger`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M15.8333 2.5H4.16663C2.98811 2.5 2.39886 2.5 2.03274 2.8435C1.66663 3.187 1.66663 3.73985 1.66663 4.84555V5.4204C1.66663 6.28527 1.66663 6.7177 1.88296 7.07618C2.09929 7.43466 2.49452 7.65715 3.28497 8.10212L5.7125 9.46865C6.24284 9.7672 6.50802 9.91648 6.69789 10.0813C7.09328 10.4246 7.33669 10.8279 7.44699 11.3226C7.49996 11.5602 7.49996 11.8382 7.49996 12.3941L7.49996 14.6187C7.49996 15.3766 7.49996 15.7556 7.7099 16.0511C7.91983 16.3465 8.2927 16.4923 9.03842 16.7838C10.6039 17.3958 11.3867 17.7018 11.9433 17.3537C12.5 17.0055 12.5 16.2099 12.5 14.6187V12.3941C12.5 11.8382 12.5 11.5602 12.5529 11.3226C12.6632 10.8279 12.9066 10.4246 13.302 10.0813C13.4919 9.91648 13.7571 9.7672 14.2874 9.46865L16.7149 8.10212C17.5054 7.65715 17.9006 7.43466 18.117 7.07618C18.3333 6.7177 18.3333 6.28527 18.3333 5.4204V4.84555C18.3333 3.73985 18.3333 3.187 17.9672 2.8435C17.6011 2.5 17.0118 2.5 15.8333 2.5Z"
            stroke="#1E2A2C"
            strokeWidth="1.5"
            className="dark:stroke-white"
          />
        </svg>
      </div>
      {showFilterOptions && (
        <div className="dropdownMenuHolder absolute bg-white dark:bg-darkThemeDropdownBg dark:shadow-darkThemeDropdownMenuShadow top-full right-0 shadow-appShadow rounded-md block z-50 w-72 py-5 mt-2 max-h-[70vh] overflow-y-auto" data-testid={`${dataTestId}-menu`}>
          <div className="flex justify-between items-center gap-2 border-b border-[#AFC3BC66] dark:border-darkThemeBorder pb-3 px-3">
            <h4 className="sortingCatagryTitle text-dark-grey dark:text-white text-xl font-semibold leading-8 tracking-[0.018px] capitalize">
              Filters
            </h4>
            <div className="flex items-center gap-4">
              <span
                className={`text-sm text-theme font-medium leading-normal cursor-pointer ${!hasDraftSelection ? "pointer-events-none opacity-50" : ""}`}
                data-testid={`${dataTestId}-clear-all-button`}
                onClick={() => {
                  clearAll()
                }}
              >
                Clear All
              </span>
              <div
                className="closeIconHolder static h-6 w-6 flex items-center justify-center cursor-pointer"
                onClick={() => setShowFilterOptions(false)}
                data-testid={`${dataTestId}-close-button`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M6.3999 5.58105C6.62599 5.58105 6.82095 5.6613 6.97998 5.82031L11.9995 10.8398L17.02 5.82031C17.1733 5.66709 17.3667 5.58941 17.5942 5.58594C17.8235 5.58247 18.0206 5.66076 18.1802 5.82031C18.3392 5.97934 18.4194 6.17431 18.4194 6.40039C18.4194 6.62647 18.3392 6.82144 18.1802 6.98047L13.1597 12L18.1802 17.0205C18.3334 17.1738 18.4111 17.3672 18.4146 17.5947C18.418 17.824 18.3397 18.0211 18.1802 18.1807C18.0212 18.3397 17.8262 18.4199 17.6001 18.4199C17.374 18.4199 17.179 18.3397 17.02 18.1807L11.9995 13.1602L6.97998 18.1807C6.82672 18.3339 6.63331 18.4116 6.40576 18.415C6.17648 18.4185 5.97936 18.3402 5.81982 18.1807C5.66078 18.0216 5.58059 17.8267 5.58057 17.6006C5.58057 17.3745 5.66082 17.1795 5.81982 17.0205L10.8394 12L5.81982 6.98047C5.66659 6.82721 5.58892 6.6338 5.58545 6.40625C5.58197 6.17697 5.66027 5.97985 5.81982 5.82031C5.97885 5.66127 6.17381 5.58107 6.3999 5.58105Z"
                    fill="#1E2A2C"
                    stroke="#1E2A2C"
                    strokeWidth="0.15"
                    className="dark:stroke-white dark:fill-white"
                  />
                </svg>
              </div>
            </div>
          </div>

          {sections.map(renderSection)}

          <div className="dropdownActionsHolder px-3 mt-5">
            <button
              className="bg-theme h-9 text-base text-white font-semibold leading-4 tracking-[0.15] hover:bg-theme/80 px-5 py-2 rounded w-full flex items-center justify-center"
              data-testid={`${dataTestId}-apply-button`}
              onClick={(e) => {
                e.stopPropagation();
                onApply?.(draftValues);
                setShowFilterOptions(false);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );

}


export default FilterDropDown;
